import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { useAuthUser } from "@/core/context/AuthUserContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import useMemberGroupTagsList from "@/product/common/member-group/common/tag/useMemberGroupTagsList"
import UnenrollProductMemberButton from "@/product/member/admin/enrollment/UnenrollProductMemberButton"
import MakeProductTeacherButton from "@/product/member/admin/list/card/more-actions/make-product-teacher/MakeProductTeacherButton"
import ManageProductMemberGroupsButton from "@/product/member/admin/list/card/more-actions/manage-groups/ManageProductMemberGroupsButton"
import { ProductMemberCardFragment$key } from "@/product/member/common/card/__generated__/ProductMemberCardFragment.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import ProductRoleTag from "@/role/ProductRoleTag"
import { useCanManageProductRole, useProductRoleLabels } from "@/role/roleUtils"
import useShowOnHoverStyles from "@assets/style/util/useShowOnHoverStyles"
import { DiscoCheckbox } from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import { Theme } from "@material-ui/core"
import usePermissions from "@utils/hook/usePermissions"
import classnames from "classnames"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"
import UserCardAdminDropdown from "../../admin/list/card/dropdown/UserCardAdminDropdown"
import UserCard from "./UserCard"

interface ProductMemberCardProps {
  testid?: string
  isSelected?: boolean
  handleSelectMember: (productMembershipId: GlobalID, userId: GlobalID) => void
  refetchMemberships: () => void
  productMembershipKey: ProductMemberCardFragment$key
  groovyDepths?: keyof Theme["palette"]["groovyDepths"]
  className?: string
}

function ProductMemberCard({
  testid,
  isSelected = false,
  handleSelectMember,
  refetchMemberships,
  productMembershipKey,
  groovyDepths,
  className,
}: ProductMemberCardProps) {
  const { authUser } = useAuthUser({ required: true })
  const experienceSettingsDrawer = useGlobalDrawer("experienceSettings")

  const productMembership = useFragment<ProductMemberCardFragment$key>(
    graphql`
      fragment ProductMemberCardFragment on ProductMembership
      @argumentDefinitions(
        canMakeTeacher: { type: "Boolean!" }
        canUnenroll: { type: "Boolean!" }
      ) {
        id
        role
        ...MakeProductTeacherButtonFragment @include(if: $canMakeTeacher)
        ...UnenrollProductMemberButtonFragment @include(if: $canUnenroll)
        ...useMemberGroupTagsList_ProductMembershipFragment
        member {
          id
          avatar
          firstName
          lastName
          ...UserCardFragment
        }
      }
    `,
    productMembershipKey
  )

  const activeProduct = useActiveProduct()!
  const permissions = usePermissions(activeProduct)
  const canUnenroll = useCanManageProductRole(
    activeProduct,
    productMembership?.role || null
  )
  const { everyoneTags: groupTags } = useMemberGroupTagsList({
    productMembershipKey: productMembership,
  })
  const showOnHoverClasses = useShowOnHoverStyles()
  const roleLabels = useProductRoleLabels()

  // This can return null if the node is deleted via @deleteRecord
  if (!productMembership) return null

  const isPathway = activeProduct.type === "pathway"
  const canEmail = permissions.has("emails.manage")
  const canManageGroups = permissions.has("member_groups.manage")
  const isMyCard = authUser.id === Relay.fromGlobalId(productMembership.member.id).id
  const canManageExperienceSettings = permissions.has("experience_settings.manage")
  const canMakeManager = !isPathway && permissions.has("product_managers.manage")
  const canMakeInstructor = !isPathway && permissions.has("product_instructors.manage")

  const roleLabel = roleLabels[productMembership.role]

  return (
    <UserCard
      testid={testid}
      className={classnames(className, showOnHoverClasses.hoverable)}
      userKey={productMembership.member}
      groovyDepths={groovyDepths}
      checkbox={
        (canEmail || canManageGroups) && (
          <DiscoCheckbox
            className={classnames({ [showOnHoverClasses.showable]: !isSelected })}
            testid={"ProductMemberCard.select-button"}
            label={null}
            onChange={() =>
              handleSelectMember(productMembership.id, productMembership.member.id)
            }
            checked={isSelected}
          />
        )
      }
      tags={[
        <ProductRoleTag key={"role"} productRole={productMembership.role} />,
        ...groupTags,
      ]}
      dropdown={
        (canManageGroups ||
          canMakeManager ||
          canMakeInstructor ||
          canUnenroll ||
          canManageExperienceSettings) && (
          <span className={showOnHoverClasses.showable}>
            <UserCardAdminDropdown
              testid={testid}
              content={
                <>
                  {canManageGroups && (
                    <ManageProductMemberGroupsButton
                      refetchMemberships={refetchMemberships}
                      productMembershipId={productMembership.id}
                    >
                      {(props) => (
                        <DiscoDropdownItem
                          {...props}
                          title={"Manage Groups"}
                          testid={`${testid}.ManageGroupsButton`}
                        />
                      )}
                    </ManageProductMemberGroupsButton>
                  )}
                  {productMembership.role === "member" ? (
                    <>
                      {canMakeManager && (
                        <MakeProductTeacherButton
                          productMembershipKey={productMembership}
                          productRole={"manager"}
                        >
                          {(props) => (
                            <DiscoDropdownItem
                              {...props}
                              title={`Make ${roleLabels.manager.singular}`}
                              testid={`${testid}.MakeManagerButton`}
                            />
                          )}
                        </MakeProductTeacherButton>
                      )}
                      {canMakeInstructor && (
                        <MakeProductTeacherButton
                          productMembershipKey={productMembership}
                          productRole={"instructor"}
                        >
                          {(props) => (
                            <DiscoDropdownItem
                              {...props}
                              title={`Make ${roleLabels.instructor.singular}`}
                              testid={`${testid}.MakeInstructorButton`}
                            />
                          )}
                        </MakeProductTeacherButton>
                      )}
                      {canUnenroll && (
                        <UnenrollProductMemberButton
                          productMembershipKey={productMembership}
                        >
                          {(props) => (
                            <DiscoDropdownItem
                              {...props}
                              title={`Unenroll ${roleLabel.singular}`}
                              testid={`${testid}.UnenrollMemberButton`}
                            />
                          )}
                        </UnenrollProductMemberButton>
                      )}
                    </>
                  ) : (
                    <>
                      {!isMyCard && canUnenroll && (
                        <UnenrollProductMemberButton
                          productMembershipKey={productMembership}
                        >
                          {(props) => (
                            <DiscoDropdownItem
                              {...props}
                              title={`Unenroll ${roleLabel.singular}`}
                              testid={`${testid}.RemoveTeacherButton`}
                            />
                          )}
                        </UnenrollProductMemberButton>
                      )}
                      {canManageExperienceSettings && (
                        <DiscoDropdownItem
                          title={"Manage Role"}
                          testid={`${testid}.ManageRoleButton`}
                          onClick={() =>
                            experienceSettingsDrawer.open({
                              drawerExperienceId: activeProduct.id,
                              experienceSettingsTab: "team",
                            })
                          }
                        />
                      )}
                    </>
                  )}
                </>
              }
            />
          </span>
        )
      }
    />
  )
}

export default ProductMemberCard
